import { useState, useEffect, useCallback, useMemo } from "react"
import { PaginationState, RowSelectionState } from "@tanstack/react-table"
import H1 from "~/ui-rtk/components/ui/typography/H1"
import {
  Button,
  DateRangePicker,
  MultiSelect,
} from "~/ui-rtk/components/ui/controls"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { DATE_FORMAT, isValidDate, usePeriod } from "~/ui-rtk/hooks/period"
import useDate from "~/ui-rtk/hooks/date"
import useConnect from "./connect"
import HomepageFunnelChart from "./components/HomepageFunnelChart"
import HomepageFunnelTable, {
  ALL_COLUMNS,
} from "./components/HomepageFunnelTable"
import BenchmarkControls from "~/ui-rtk/components/ui/controls/BenchmarkControls"
import {
  BenchmarkTimeframe,
  PlottedRow,
  PLOT_ROW_COLORS,
  MAX_PLOTTED_ROWS,
} from "../types"
import useUrlColumnKeys from "~/ui-rtk/hooks/url-column-keys"
import type { TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import type { TableVisualizationColumn } from "~/ui-rtk/constants/table-visualization-types"
import TableFiltersBar from "~/ui-rtk/components/ui/controls/FiltersBar/TableFiltersBar"
import useUrlColumnFilters from "~/ui-rtk/hooks/url-column-filters"
import {
  buildFunnelExportColumns,
  rateColumns,
  useBenchmarks,
  useFunnelCsvExport,
  type FunnelExportMapping,
} from "../hooks"
import useCubeFiltersFromColumnFilters from "~/ui-rtk/hooks/cube-filters-from-column-filters"

type DeviceCategory = "ALL" | "DESKTOP" | "MOBILE" | "TABLET"

const DEVICE_CATEGORY_OPTIONS = [
  { id: "ALL" as DeviceCategory, label: "All Traffic" },
  { id: "DESKTOP" as DeviceCategory, label: "Desktop Traffic" },
  { id: "MOBILE" as DeviceCategory, label: "Mobile Traffic" },
  { id: "TABLET" as DeviceCategory, label: "Tablet Traffic" },
]

const URL_PREFIX = "homepage_funnel"
const CUBE = "company_web_analytics_homepage_funnel"

const FILTER_KEY_MAPPING: Record<string, string> = {
  deviceCategory: `${CUBE}.device_category`,
  homepageViewers: `${CUBE}.homepage_viewers`,
  shoppingPageViewers: `${CUBE}.shopping_page_viewers`,
  addersToCart: `${CUBE}.adders_to_cart`,
  purchasers: `${CUBE}.purchasers`,
}

/** Visible column → CSV column(s); the table shows rates without abandon. */
const EXPORT_COLUMNS: FunnelExportMapping = {
  deviceCategory: [
    { header: "Device Category", member: `${CUBE}.device_category` },
  ],
  homepageViewers: [
    { header: "Homepage Viewers", member: `${CUBE}.homepage_viewers` },
  ],
  shoppingPageViewers: [
    {
      header: "Shopping Page Viewers",
      member: `${CUBE}.shopping_page_viewers`,
    },
  ],
  addersToCart: [
    { header: "Adders to Cart", member: `${CUBE}.adders_to_cart` },
  ],
  purchasers: [{ header: "Purchasers", member: `${CUBE}.purchasers` }],
  homepageToShopConversion: rateColumns(
    "Homepage → Shop Rate",
    `${CUBE}.shopping_page_viewers`,
    `${CUBE}.homepage_viewers`,
    { withAbandon: false },
  ),
  shopToCartConversion: rateColumns(
    "Shop → Cart Rate",
    `${CUBE}.adders_to_cart`,
    `${CUBE}.shopping_page_viewers`,
    { withAbandon: false },
  ),
  homepageToCartConversion: rateColumns(
    "Homepage → Cart Rate",
    `${CUBE}.adders_to_cart`,
    `${CUBE}.homepage_viewers`,
    { withAbandon: false },
  ),
  cartToPurchaseConversion: rateColumns(
    "Cart → Purchase Rate",
    `${CUBE}.purchasers`,
    `${CUBE}.adders_to_cart`,
    { withAbandon: false },
  ),
  shopToPurchaseConversion: rateColumns(
    "Shop → Purchase Rate",
    `${CUBE}.purchasers`,
    `${CUBE}.shopping_page_viewers`,
    { withAbandon: false },
  ),
  homepageToPurchaseConversion: rateColumns(
    "Homepage → Purchase Rate",
    `${CUBE}.purchasers`,
    `${CUBE}.homepage_viewers`,
    { withAbandon: false },
  ),
}

export default function HomepageFunnelPage() {
  const dayjs = useDate()
  const [activeDeviceCategory, setActiveDeviceCategory] =
    useState<DeviceCategory>("ALL")
  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  // Benchmark state
  const [benchmarkEnabled, setBenchmarkEnabled] = useState(false)
  const [benchmarkTimeframe, setBenchmarkTimeframe] =
    useState<BenchmarkTimeframe>("6mo")

  // Plot rows state
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [plottedRows, setPlottedRows] = useState<PlottedRow[]>([])

  const {
    period: dateRange,
    comparePeriod: compareRange,
    setPeriod,
    setComparePeriod,
  } = usePeriod()

  // Column selection
  const defaultColumnKeys = useMemo(
    () =>
      ALL_COLUMNS.filter(
        (column: TableVisualizationColumn & { disabled?: boolean }) =>
          !column.disabled,
      ).map((column: TableVisualizationColumn) => column.key),
    [],
  )

  const { columnKeys, setColumnKeys } = useUrlColumnKeys(
    URL_PREFIX,
    defaultColumnKeys,
  )

  const columnOptions = useMemo(
    () =>
      ALL_COLUMNS.map(
        (column: TableVisualizationColumn) =>
          ({
            value: column.key,
            label: column.header,
          }) as TMultiSelectOption,
      ),
    [],
  )

  const columnConfigFiltered = useMemo(
    () =>
      ALL_COLUMNS.filter(({ key }: { key: string }) =>
        (columnKeys as string[])?.includes(key),
      ).map(
        ({
          _disabled,
          ...rest
        }: TableVisualizationColumn & { _disabled?: boolean }) => rest,
      ),
    [columnKeys],
  )

  // Column filters
  const { columnFilters } = useUrlColumnFilters(URL_PREFIX)

  const cubeFilters = useMemo(
    () =>
      useCubeFiltersFromColumnFilters(null, columnFilters, FILTER_KEY_MAPPING),
    [columnFilters],
  )

  // Reset selection on filter or page change
  useEffect(() => {
    setRowSelection({})
    setPlottedRows([])
  }, [activeDeviceCategory])

  // Reset pagination when filter changes
  useEffect(() => {
    setPaginationState(prev => ({ ...prev, pageIndex: 0 }))
  }, [activeDeviceCategory])

  const {
    isLoading: isFunnelLoading,
    funnelData,
    compareFunnelData,
    tableData,
    compareTableData,
    totalCount,
    tableQuery,
  } = useConnect(
    activeDeviceCategory,
    dateRange,
    paginationState,
    compareRange ?? undefined,
    cubeFilters.length > 0 ? cubeFilters : undefined,
  )

  // Fetch benchmark data filtered by active device category
  const { benchmarks, isLoading: isBenchmarkLoading } = useBenchmarks(
    "homepage",
    "TOTAL",
    benchmarkTimeframe,
    benchmarkEnabled,
    activeDeviceCategory,
  )

  // Combined loading state
  const isLoading = isFunnelLoading || (benchmarkEnabled && isBenchmarkLoading)

  // Enforce max 3 row selection
  const handleRowSelectionChange = useCallback(
    (
      updaterOrValue:
        | RowSelectionState
        | ((prev: RowSelectionState) => RowSelectionState),
    ) => {
      setRowSelection(prev => {
        const next =
          typeof updaterOrValue === "function"
            ? updaterOrValue(prev)
            : updaterOrValue
        const selectedKeys = Object.keys(next).filter(k => next[k])
        if (selectedKeys.length > MAX_PLOTTED_ROWS) {
          return prev
        }
        return next
      })
    },
    [],
  )

  const handlePlotRows = useCallback(() => {
    const selectedIndices = Object.keys(rowSelection)
      .filter(k => rowSelection[k])
      .map(Number)

    const rows: PlottedRow[] = selectedIndices
      .map((idx, i) => {
        const row = tableData[idx]
        if (!row) return null

        const stages = [
          { label: "Homepage Viewers", value: row.homepageViewers },
          { label: "Shopping Page Viewers", value: row.shoppingPageViewers },
          { label: "Adders to Cart", value: row.addersToCart },
          { label: "Purchasers", value: row.purchasers },
        ]

        const compareRow = compareTableData?.[idx]
        const compareStages = compareRow
          ? [
              { label: "Homepage Viewers", value: compareRow.homepageViewers },
              {
                label: "Shopping Page Viewers",
                value: compareRow.shoppingPageViewers,
              },
              { label: "Adders to Cart", value: compareRow.addersToCart },
              { label: "Purchasers", value: compareRow.purchasers },
            ]
          : undefined

        return {
          id: row.deviceCategory,
          label: row.deviceCategory,
          color: PLOT_ROW_COLORS[i % PLOT_ROW_COLORS.length],
          stages,
          compareStages,
        }
      })
      .filter(Boolean) as PlottedRow[]

    setPlottedRows(rows)
  }, [rowSelection, tableData, compareTableData])

  const selectedCount = Object.keys(rowSelection).filter(
    k => rowSelection[k],
  ).length

  const handleChangePeriods = ({
    start,
    end,
    compareStart,
    compareEnd,
  }: {
    start: Date
    end: Date
    compareStart?: Date | null
    compareEnd?: Date | null
  }) => {
    setPeriod(dayjs(start).format(DATE_FORMAT), dayjs(end).format(DATE_FORMAT))

    if (compareStart && compareEnd) {
      setComparePeriod(
        dayjs(compareStart).format(DATE_FORMAT),
        dayjs(compareEnd).format(DATE_FORMAT),
      )
    } else {
      setComparePeriod()
    }
  }

  // Export CSV — the same worker export the Page Path report uses. It re-runs
  // the table's OWN request (from useConnect) exactly as fired, current page
  // included, so the file holds the rows on screen under the same filters,
  // sort and compare period; visible columns are translated to the raw
  // members / derived rates the worker can read off a Cube row.
  const exportColumns = useMemo(
    () => buildFunnelExportColumns(columnConfigFiltered, EXPORT_COLUMNS),
    [columnConfigFiltered],
  )
  const {
    exportCSV: handleExportCSV,
    isExporting,
    exportProgress,
  } = useFunnelCsvExport({
    tableQuery,
    columns: exportColumns,
    filename: "homepage-funnel",
  })

  return (
    <div className="h-full px-4 py-2 bg-basic-black text-basic-white">
      <div className="px-4">
        <H1>Homepage Funnel</H1>

        <div className="my-system-lg flex items-center justify-between">
          <div className="join">
            {DEVICE_CATEGORY_OPTIONS.map(({ id, label }) => (
              <Button
                variant={{ variant: "solid", color: "blue" }}
                key={id}
                title={label}
                className={cn(
                  "join-item border border-system-blue-600 h-system-2xl px-system-lg py-system-sm rounded-system-md",
                  { "bg-system-blue-800": activeDeviceCategory !== id },
                  { "bg-system-blue-600": activeDeviceCategory === id },
                )}
                onClick={() => setActiveDeviceCategory(id)}
              >
                <span
                  className={cn("font-[Lato]", {
                    "text-base text-system-gray-300 font-normal":
                      activeDeviceCategory !== id,
                    "text-white font-semibold": activeDeviceCategory === id,
                  })}
                >
                  {label}
                </span>
              </Button>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <BenchmarkControls
              enabled={benchmarkEnabled}
              timeframe={benchmarkTimeframe}
              onEnabledChange={setBenchmarkEnabled}
              onTimeframeChange={setBenchmarkTimeframe}
            />
            {dateRange?.[0] &&
              dateRange?.[1] &&
              isValidDate(dateRange[0]) &&
              isValidDate(dateRange[1]) && (
                <DateRangePicker
                  start={dayjs(dateRange[0]).toDate()}
                  end={dayjs(dateRange[1]).toDate()}
                  compareStart={
                    compareRange?.[0] && isValidDate(compareRange[0])
                      ? dayjs(compareRange[0]).toDate()
                      : undefined
                  }
                  comparisonEnabled={true}
                  compareEnd={
                    compareRange?.[1] && isValidDate(compareRange[1])
                      ? dayjs(compareRange[1]).toDate()
                      : undefined
                  }
                  onChange={handleChangePeriods}
                />
              )}
          </div>
        </div>
      </div>

      <div className="space-y-8">
        {/* Funnel Chart Section */}
        <div className="px-4">
          <div className="bg-system-blue-800 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Conversion Funnel</h2>
            <HomepageFunnelChart
              data={funnelData}
              compareData={compareFunnelData}
              isLoading={isLoading}
              benchmarks={benchmarks}
              showBenchmarks={benchmarkEnabled}
              plottedRows={plottedRows}
            />
          </div>
        </div>
        {/* Table Section */}
        <div className=" rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Homepage Funnel Details</h2>
            <div className="flex items-center gap-4">
              <MultiSelect
                label="Columns"
                options={columnOptions}
                value={columnOptions.filter((option: TMultiSelectOption) =>
                  (columnKeys as string[]).includes(option.value),
                )}
                onChange={(options: TMultiSelectOption[]) =>
                  setColumnKeys(options.map(({ value }) => value))
                }
              />
              <TableFiltersBar
                id={URL_PREFIX}
                columnConfig={columnConfigFiltered}
              />
            </div>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <Button
              variant={{ variant: "solid", color: "blue" }}
              className="border border-system-blue-600 h-system-2xl px-system-lg py-system-sm rounded-system-md bg-system-blue-800"
              onClick={handlePlotRows}
              disabled={selectedCount === 0 && plottedRows.length === 0}
            >
              <span className="font-[Lato] text-sm">
                {selectedCount === 0 && plottedRows.length > 0
                  ? "Show Aggregate"
                  : `Plot Rows${selectedCount > 0 ? ` (${selectedCount})` : ""}`}
              </span>
            </Button>
            <span className="text-xs text-system-gray-500">
              Select up to {MAX_PLOTTED_ROWS} rows to plot on chart
            </span>
          </div>
          <HomepageFunnelTable
            data={tableData}
            compareData={compareTableData}
            compareUniqueKey="deviceCategory"
            isLoading={isLoading}
            paginationState={paginationState}
            setPaginationState={setPaginationState}
            totalCount={totalCount}
            enableRowSelection={true}
            rowSelection={rowSelection}
            onRowSelectionChange={handleRowSelectionChange}
            columnConfig={columnConfigFiltered}
            csvExport={handleExportCSV}
            isExporting={isExporting}
            exportProgress={exportProgress}
          />
        </div>
      </div>
    </div>
  )
}
